import { DataTable } from '@/components/data-table/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { CalculationType, DeployedDevice, MeasurementGroupLoaderResponse } from './types';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb.tsx';
import { BreadcrumbRouterLink, RouterLink } from '@/components/link/Link';
import { ROUTES } from '@/constants';
import ProcessObjectFiles from './ProcessObjectFiles';
import { LpohTabs, LpohTabsProps } from '@/components/tabs/LpohTabs';
import { mergeQueryParams } from '@/utils/merge-query-params';
import { Params } from '@/utils/types';
import FieldReports from './field-report/field-reports-list/FieldReports';
import { Button } from '@/components/ui/button';
import MeasurementGroupGenerationTab from './generation/MeasurementGroupGenerationTab';
import { encodeDateToURL } from '@/utils/dates';
import { addDays, subDays } from 'date-fns';
import MeasurementGroupOverview from './MeasurementGroupOverview';
import useTitle from '@/hooks/useTitle.ts';

const tabOptions: LpohTabsProps['tabOptions'] = [
	{
		value: 'home',
		label: 'Home'
	},
	{
		value: 'fieldReports',
		label: 'Field Reports'
	},
	{
		value: 'generation',
		label: 'Generation'
	}
];

enum GenerationTabQueryParams {
	CalculationType = 'calculationType',
	Priority = 'priority',
	From = 'from',
	To = 'to'
}

export const MeasurementGroup = () => {
	const {
		measurementGroup: { data: measurementGroupData },
		fileList: { data: fileListData }
	} = useLoaderData() as MeasurementGroupLoaderResponse;

	useTitle(measurementGroupData.name)

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const tabValue = searchParams.get(Params.tab) ?? tabOptions[0].value;

	const handleTabChange = (value: string) => {
		const additionalSearchParams: Record<string, string> = {};
		if (value === 'generation') {
			const defaultFrom = encodeDateToURL(subDays(new Date(), 3));
			const defaultTo = encodeDateToURL(addDays(new Date(), 1));

			if (defaultFrom && defaultTo) {
				additionalSearchParams[GenerationTabQueryParams.From] = defaultFrom;
				additionalSearchParams[GenerationTabQueryParams.To] = defaultTo;
			}

			additionalSearchParams[GenerationTabQueryParams.CalculationType] = CalculationType.Power;
		}

		const queryString = mergeQueryParams(searchParams, {
			[Params.tab]: value,
			[Params.search]: undefined,
			[Params.page]: undefined,
			[Params.pageSize]: undefined,
			[Params.sortBy]: undefined,
			[Params.sortDirection]: undefined,

			[GenerationTabQueryParams.From]: undefined,
			[GenerationTabQueryParams.To]: undefined,
			[GenerationTabQueryParams.CalculationType]: undefined,
			[GenerationTabQueryParams.Priority]: undefined,

			...additionalSearchParams
		});

		navigate(`?${queryString}`);
	};

	const columns: ColumnDef<DeployedDevice>[] = [
		{
			accessorKey: 'id',
			header: 'MS#'
		},
		{
			accessorKey: 'poId',
			header: 'PO#'
		},
		{
			accessorKey: 'site',
			header: 'Site'
		}
	];

	return (
		<div className="flex flex-col gap-5">
			<div className="w-full flex justify-between">
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbRouterLink to={ROUTES.measurementGroups}>
								Measurement Groups
							</BreadcrumbRouterLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbPage>{measurementGroupData.name}</BreadcrumbPage>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>

				<Button asChild size="sm" variant="ghost" className="w-fit text-xs">
					<RouterLink
						to="calibration"
						className="bg-slate-200 dark:bg-slate-800 p-2 rounded text-xs items-center justify-center gap-2 flex font-medium hover:text-gray-500 dark:hover:text-gray-300"
					>
						Calibration operations
					</RouterLink>
				</Button>
			</div>
			<LpohTabs onValueChange={handleTabChange} tabOptions={tabOptions} value={tabValue}>
				<>
					<div className="w-full flex flex-col gap-4">
						<MeasurementGroupOverview measurementGroup={measurementGroupData} />

						<p className="font-bold">Devices</p>
						<DataTable columns={columns} data={measurementGroupData.devices} />
					</div>
					<ProcessObjectFiles
						measurementGroupId={measurementGroupData.id}
						measurementGroupName={measurementGroupData.name}
						files={fileListData}
					/>
				</>
				<FieldReports />

				<MeasurementGroupGenerationTab />
			</LpohTabs>
		</div>
	);
};
